import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Inject, Injectable, InjectionToken, Optional } from '@angular/core';
import { datadogRum } from '@datadog/browser-rum';
import { Observable, throwError, timer } from 'rxjs';
import { concatMap, filter, mergeMap, retryWhen, take, timeout } from 'rxjs/operators';
import { ConnectivityService } from './connectivity.service';
import {
  computeBackoffMs,
  isRetriableMethod,
  isRetriableStatus,
  MAX_ATTEMPTS,
  parseRetryAfterMs,
  REQUEST_TIMEOUT_MS,
  stripQuery,
} from './retry-policy';

/**
 * URL patterns that the retry interceptor should leave alone — neither timed out nor retried.
 * Use for long-poll endpoints, large uploads, and presigned third-party URLs whose timing
 * characteristics differ from normal API calls. Provide via app module:
 *
 *   { provide: RETRY_BYPASS_URLS, useValue: [/\/upload\b/, /amazonaws\.com/] }
 */
export const RETRY_BYPASS_URLS = new InjectionToken<RegExp[]>('RETRY_BYPASS_URLS');

/**
 * Default bypass patterns applied even when no `RETRY_BYPASS_URLS` provider is set.
 * Matches URLs that other interceptors already exempt from auth/normal handling.
 */
const DEFAULT_BYPASS_PATTERNS: RegExp[] = [
  /amazonaws\.com/i,
  /\bimage-test\b/i,
  /\/upload(?:\/|\?|$)/i,
  /\/media-upload(?:\/|\?|$)/i,
];

@Injectable()
export class RetryInterceptor implements HttpInterceptor {
  private readonly bypass: RegExp[];

  constructor(
    private readonly connectivity: ConnectivityService,
    @Optional() @Inject(RETRY_BYPASS_URLS) extraBypass: RegExp[] | null
  ) {
    this.bypass = DEFAULT_BYPASS_PATTERNS.concat(extraBypass || []);
  }

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (this.shouldBypass(req.url)) {
      return next.handle(req);
    }

    return next.handle(req).pipe(
      timeout(REQUEST_TIMEOUT_MS),
      retryWhen((errors$) =>
        errors$.pipe(
          concatMap((err: unknown, index) => {
            // `index` is the 0-based count of errors seen so far.
            // `attemptsCompleted` is the number of HTTP requests that have failed.
            // We allow up to MAX_ATTEMPTS total requests (initial + retries).
            const attemptsCompleted = index + 1;
            const isTimeout = isTimeoutError(err);
            const status = isTimeout ? 0 : extractStatus(err);

            const retriable =
              isRetriableMethod(req.method) &&
              (isTimeout || isRetriableStatus(status)) &&
              attemptsCompleted < MAX_ATTEMPTS;

            if (!retriable) {
              return throwError(isTimeout ? toNetworkError(req.url) : err);
            }

            const retryAfterMs = !isTimeout ? readRetryAfter(err) : null;
            const delayMs = retryAfterMs ?? computeBackoffMs(attemptsCompleted - 1);

            safeAddAction('http_retry', {
              url: stripQuery(req.urlWithParams),
              method: req.method,
              status,
              attemptsCompleted,
              delayMs,
              isTimeout,
            });

            return this.connectivity.isOnline$.pipe(
              filter((online) => online),
              take(1),
              mergeMap(() => timer(delayMs))
            );
          })
        )
      )
    );
  }

  private shouldBypass(url: string): boolean {
    return this.bypass.some((pattern) => pattern.test(url));
  }
}

function isTimeoutError(err: unknown): boolean {
  return !!err && typeof err === 'object' && (err as { name?: string }).name === 'TimeoutError';
}

function extractStatus(err: unknown): number {
  if (err instanceof HttpErrorResponse) return err.status ?? 0;
  if (err && typeof err === 'object' && typeof (err as { status?: number }).status === 'number') {
    return (err as { status: number }).status;
  }
  return 0;
}

function readRetryAfter(err: unknown): number | null {
  if (!(err instanceof HttpErrorResponse) || err.headers == null) return null;
  return parseRetryAfterMs(err.headers.get('Retry-After'), Date.now());
}

function toNetworkError(url: string): HttpErrorResponse {
  return new HttpErrorResponse({
    status: 0,
    statusText: 'Timeout',
    url,
    error: { reason: 'Request timed out' },
  });
}

function safeAddAction(name: string, ctx: Record<string, unknown>): void {
  try {
    datadogRum.addAction(name, ctx);
  } catch {
    /* RUM not initialized — addAction is a no-op in v2 but guard anyway */
  }
}
